/**
 * Enhanced Error Tracking for Core Web App
 *
 * Provides rich error context to PostHog to match backend error template format.
 * Captures user, corporate, site, page URL, and session recording information.
 */

import posthog from 'posthog-js'
import useAuthStore from '_stores/auth.store'
import useCorporateStore from '_stores/corporate.store'
import useSiteStore from '_stores/site.store'
import { redactPaymentApprovalToken } from '_utils/payment-approval-path.util'

export const ERROR_SCREEN_FEATURE = 'error-screen'

export const ErrorScreen = {
  Root: 'root',
  Component: 'component',
} as const

export type ErrorScreen = (typeof ErrorScreen)[keyof typeof ErrorScreen]

export function toTrackableError(value: unknown): Error {
  if (value instanceof Error) {
    return value
  }

  const routeError = value as { statusText?: string; status?: number; message?: string } | null
  const message =
    typeof value === 'string'
      ? value
      : (routeError?.statusText ?? routeError?.message ?? 'Unknown error')

  const error = new Error(message)
  error.name = routeError?.status ? `RouteError(${routeError.status})` : 'UnknownError'
  return error
}

export interface EnhancedErrorContext {
  userId?: string
  feature?: string
  action?: string
  componentName?: string
  metadata?: Record<string, any>
}

function isLocalhost(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('.localhost')
  )
}

function generateTraceId(): string {
  // Get PostHog session ID if available
  const sessionId =
    posthog?.get_session_id?.() ||
    `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`

  // Generate unique request ID
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

  return `${sessionId}.${requestId}`
}

/**
 * Get the actual domain (not localhost) for the current page.
 * In development, construct the production-like domain from subdomain.
 */
function getActualPageUrl(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const { pathname, search, hash } = window.location

  // If not localhost, return the actual URL
  if (!isLocalhost()) {
    return window.location.href
  }

  // In localhost, construct a production-like URL from subdomain
  const hostname = window.location.hostname
  const subdomain = hostname.split('.')[0]

  // Default to elite subdomain if none specified
  const actualSubdomain = subdomain && subdomain !== 'localhost' ? subdomain : 'elite'

  // Construct production-like URL
  // Using the pattern from POSTHOG_HOST environment variable
  const actualHost =
    process.env.NODE_ENV === 'production'
      ? `https://${actualSubdomain}.hapana.com`
      : `https://${actualSubdomain}.hapana-dev.com`

  return `${actualHost}${pathname}${search}${hash}`
}

/**
 * Parse browser name and OS from user agent string.
 * Kept simple — just enough for Slack readability.
 */
function parseUserAgent(ua: string): { browser: string; os: string } {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Unknown'

  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : /CrOS/.test(ua)
              ? 'ChromeOS'
              : 'Unknown'

  return { browser, os }
}

/**
 * Get rich context for error tracking from Zustand stores
 */
function getErrorContext() {
  // Get current state from stores (using getState() for synchronous access)
  const authState = useAuthStore.getState()
  const corporateState = useCorporateStore.getState()
  const siteState = useSiteStore.getState()

  const user = authState.user
  const corporate = corporateState.corporate?.corporateBySubdomain
  const site = siteState.currentSite

  return {
    // User context
    user_id: user?.uid,
    user_email: user?.email,
    user_name: user?.displayName,

    // Corporate context
    corporate_id: corporate?.corporateId,
    corporate_name: corporate?.name,
    corporate_slug: corporate?.urlPrefix,

    // Site context
    site_id: site?.siteId,
    site_name: site?.name,
    site_slug: site?.slug,

    // Page context
    page_url: redactPaymentApprovalToken(getActualPageUrl()),
    page_path:
      typeof window !== 'undefined' ? redactPaymentApprovalToken(window.location.pathname) : '',

    // Device context
    device: navigator?.userAgent || 'Unknown',
    ...parseUserAgent(navigator?.userAgent || ''),

    // Location (if available from browser)
    location: '',
  }
}

// Dedup: same error message reported at most once per 30s.
// Guards against re-render loops (e.g. GQty retrying a broken query on every
// component render) flooding PostHog and the /v2/fe-errors Slack webhook.
const _recentErrors = new Map<string, number>()
const ERROR_DEDUP_MS = 30_000

function isDuplicateError(errorName: string, message: string): boolean {
  // Key on name:message so generic messages like "Internal Server Error" from
  // different error types (GQtyError, GQtyNetworkError, TypeError…) are kept distinct.
  const key = `${errorName}:${message}`
  const now = Date.now()
  const last = _recentErrors.get(key)
  if (last !== undefined && now - last < ERROR_DEDUP_MS) {
    return true
  }
  _recentErrors.set(key, now)
  if (_recentErrors.size > 50) {
    const cutoff = now - ERROR_DEDUP_MS
    for (const [k, t] of _recentErrors) {
      if (t < cutoff) {
        _recentErrors.delete(k)
      }
    }
  }
  return false
}

/**
 * Track enhanced errors with full context matching backend error template
 */
export function trackEnhancedError(error: Error, context?: EnhancedErrorContext): void {
  if (!posthog || typeof posthog.capture !== 'function') {
    console.error('PostHog unavailable:', error)
    return
  }

  // Skip benign browser-level warnings that are not actionable
  if (error.message?.includes('ResizeObserver loop')) {
    return
  }

  // Skip error tracking on localhost
  if (isLocalhost()) {
    console.error('Error (localhost - not tracked):', error)
    return
  }

  // Skip if same error was already reported within the dedup window.
  // This prevents a broken GQty query or render loop from flooding fe-errors.
  if (isDuplicateError(error.name, error.message)) {
    return
  }

  const traceId = generateTraceId()
  const errorContext = getErrorContext()

  // Get session recording URL
  const sessionRecordingUrl = posthog.get_session_replay_url
    ? posthog.get_session_replay_url()
    : undefined

  const errorData = {
    // PostHog Error Tracking required fields
    $exception_type: error.name,
    $exception_message: error.message,
    $exception_stack_trace_raw: error.stack,

    // Error details (custom fields for PostHog events/queries)
    error_message: error.message,
    error_stack: error.stack,
    error_type: error.name,

    // Trace and session
    trace_id: traceId,
    session_recording_url: sessionRecordingUrl,

    // Rich context (matching backend template)
    ...errorContext,

    // Additional context from caller
    feature: context?.feature,
    action: context?.action,
    ...context?.metadata,

    // Timestamp
    timestamp: new Date().toISOString(),
  }

  posthog.capture('$exception', errorData)

  // Also POST to backend so errors appear in Slack with the same rich Block Kit
  // format as backend errors, instead of the broken PostHog webhook template.
  const apiBaseUrl = (import.meta as any).env?.VITE_API_URL
  if (apiBaseUrl) {
    fetch(`${apiBaseUrl}/v2/fe-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        errorMessage: error.message,
        errorType: error.name,
        pageUrl: errorContext.page_url,
        userId: errorContext.user_id,
        userEmail: errorContext.user_email,
        corporateId: errorContext.corporate_id,
        corporateName: errorContext.corporate_name,
        siteId: errorContext.site_id,
        siteName: errorContext.site_name,
        device: errorContext.device,
        browser: (errorContext as any).browser,
        os: (errorContext as any).os,
        componentName: context?.componentName,
        errorScreen: context?.metadata?.error_screen,
        isChunkLoadError: context?.metadata?.is_chunk_load_error === true,
        traceId,
        sessionRecordingUrl,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {}) // fire-and-forget — never let error reporting crash the app
  }

  // Also log to console for debugging
  console.error('Error tracked:', {
    message: error.message,
    trace_id: traceId,
    user: errorContext.user_email,
    corporate: errorContext.corporate_name,
    page: errorContext.page_url,
  })
}

/**
 * Track enhanced API errors with full context
 */
export function trackEnhancedAPIError(
  endpoint: string,
  statusCode: number,
  errorMessage: string,
  context?: EnhancedErrorContext
): void {
  if (!posthog || typeof posthog.capture !== 'function') {
    return
  }

  // Skip error tracking on localhost
  if (isLocalhost()) {
    console.error('API Error (localhost - not tracked):', { endpoint, statusCode, errorMessage })
    return
  }

  const traceId = generateTraceId()
  const errorContext = getErrorContext()

  // Get session recording URL
  const sessionRecordingUrl = posthog.get_session_replay_url
    ? posthog.get_session_replay_url()
    : undefined

  posthog.capture('api.request_failed', {
    // API details
    endpoint,
    status_code: statusCode,
    error_message: errorMessage,

    // Trace and session
    trace_id: traceId,
    session_recording_url: sessionRecordingUrl,

    // Rich context (matching backend template)
    ...errorContext,

    // Additional context
    retry_attempted: false,
    ...context,

    // Timestamp
    timestamp: new Date().toISOString(),
  })

  // Also log to console for debugging
  console.error('API Error tracked:', {
    endpoint,
    status_code: statusCode,
    message: errorMessage,
    trace_id: traceId,
    user: errorContext.user_email,
    corporate: errorContext.corporate_name,
  })
}

/**
 * Create error boundary handler with enhanced tracking
 */
export function createEnhancedErrorBoundaryHandler(componentName: string) {
  return (error: Error, errorInfo: React.ErrorInfo) => {
    trackEnhancedError(error, {
      feature: componentName,
      componentName,
      metadata: {
        componentStack: errorInfo.componentStack,
      },
    })
  }
}
